import type { AxiosError } from 'axios'

type ErrorPayload = {
  detail?: string
  code?: string
  message?: string
  non_field_errors?: string[]
  [key: string]: unknown
}

export class ApiRequestError extends Error {
  status?: number
  code?: string
  fieldErrors?: Record<string, string[]>
  raw?: ErrorPayload
  isDomainRestriction = false

  constructor(message: string, options?: Partial<ApiRequestError>) {
    super(message)
    this.name = 'ApiRequestError'
    Object.assign(this, options)
  }
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item))
  if (typeof value === 'string') return [value]
  return []
}

function collectFieldErrors(payload: ErrorPayload): Record<string, string[]> {
  const result: Record<string, string[]> = {}

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (['detail', 'code', 'message', 'non_field_errors'].includes(key)) return
    const messages = asStringArray(value)
    if (messages.length > 0) {
      result[key] = messages
    }
  })

  return result
}

function hasDomainRestrictionSignal(payload: ErrorPayload, message: string): boolean {
  const detailText = String(payload?.detail || '').toLowerCase()
  const codeText = String(payload?.code || '').toLowerCase()
  const messageText = String(message || '').toLowerCase()
  const emailErrors = asStringArray(payload?.email).join(' ').toLowerCase()

  return (
    codeText.includes('domain') ||
    detailText.includes('domain') ||
    detailText.includes('staff') ||
    detailText.includes('lwegatech') ||
    emailErrors.includes('domain') ||
    emailErrors.includes('lwegatech') ||
    messageText.includes('domain')
  )
}

export function toApiRequestError(error: unknown, fallbackMessage: string): ApiRequestError {
  if (error instanceof ApiRequestError) return error

  const axiosError = error as AxiosError<ErrorPayload>
  const payload = (axiosError?.response?.data || {}) as ErrorPayload

  const message =
    payload?.detail ||
    payload?.message ||
    payload?.non_field_errors?.[0] ||
    (typeof axiosError?.message === 'string' ? axiosError.message : fallbackMessage)

  return new ApiRequestError(message || fallbackMessage, {
    status: axiosError?.response?.status,
    code: payload?.code,
    fieldErrors: collectFieldErrors(payload),
    raw: payload,
    isDomainRestriction: hasDomainRestrictionSignal(payload, message || fallbackMessage),
  })
}

export function getUiErrorMessage(error: unknown, fallbackMessage: string): string {
  const normalized = toApiRequestError(error, fallbackMessage)
  return normalized.message || fallbackMessage
}

